import { Empty, Flex, Form, notification } from "antd";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
    getApiError,
    getMarketInfoCollection,
    postSchedulerCreateRoute,
} from "tm-shared/api";
import { Market, Schedule, UserRole } from "tm-shared/types";
import { buildRoutePath } from "tm-shared/utils";

import BaseButton from "@/components/BaseButton";
import Header from "@/components/Header";
import { MainContainer } from "@/components/Layout/Styled";
import Meta from "@/components/Meta";
import { BlockLoader } from "@/components/UI/BlockLoader";
import { ROUTES } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import useAsyncAction from "@/hooks/useAsyncAction";
import { MultiSelectorHeader } from "@/pages/SV/RouteManagement/RouteEditor/components/MultiSelectorHeader";

import FiltersPanel from "./components/FiltersPanel";
import { ModalsContainer } from "./components/ModalsContainer";
import RouteEditorForm, {
    RouteEditorFormValues,
} from "./components/RouteEditorForm";
import { ScheduleList } from "./components/ScheduleList";
import { useScheduleSelection } from "./hooks";
import {
    RouteEditorModal,
    RouteEditorModalState,
    ScheduleMenuConfig,
} from "./RouteEditor.types";

export const RouteEditorCreate: FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();

    const { id, role } = useAppSelector((s) => s.user);
    const { team, currentScheduleList } = useAppSelector((s) => s.sv);
    const [markets, setMarkets] = useState<Record<string, Market>>({});

    const [modalState, setModalState] = useState<RouteEditorModalState>(null);
    const [schedulesLoading, setSchedulesLoading] = useState(false);

    const [routeEditorForm] = Form.useForm<RouteEditorFormValues>();

    const handleCloseModal = useCallback(() => {
        setModalState(null);
    }, []);

    useEffect(() => {
        if (location?.state?.formValues) {
            routeEditorForm.setFieldsValue(location.state.formValues);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key, routeEditorForm]);

    const marketIds = useMemo(() => {
        if (!currentScheduleList.length) return [];

        return currentScheduleList.map((i) => i.marketId).sort();
    }, [currentScheduleList]);

    const hasMissingMarket = useMemo(() => {
        if (!marketIds.length) return false;

        return marketIds.some((id) => !markets[id]);
    }, [marketIds, markets]);

    useEffect(() => {
        const load = async () => {
            if (!hasMissingMarket) return;

            try {
                setSchedulesLoading(true);
                const { result: marketsData } = await getMarketInfoCollection({
                    marketIds,
                });

                const marketsMap = (marketsData ?? []).reduce<
                    Record<string, Market>
                >((acc, market) => {
                    acc[market.id] = market;
                    return acc;
                }, {});

                setMarkets(marketsMap);
            } finally {
                setSchedulesLoading(false);
            }
        };
        load();
    }, [dispatch, marketIds, hasMissingMarket]);

    const {
        filtersForm,
        watchedFilters,
        multiSelectMode,
        selectedSchedules,
        sortedAndFilteredSchedules,

        enterMultiSelectMode,
        exitMultiSelectMode,

        handleSelectSchedule,
        handleSelectAll,
        handleClearSelection,
    } = useScheduleSelection({ markets });

    const handleAddMarketClick = () => {
        navigate(ROUTES.RouteManagementCreateAddMarket, {
            state: { formValues: routeEditorForm.getFieldsValue() },
        });
    };

    const [handleSave, saveLoading] = useAsyncAction(async () => {
        const { routeAgent, routeName } = routeEditorForm.getFieldsValue();

        try {
            await postSchedulerCreateRoute({
                ownerId: id,
                name: routeName,
                agentId: routeAgent,
                schedules: currentScheduleList,
                roleSlug: role === UserRole.Sv ? UserRole.Ta : UserRole.Md,
                mainRegionId: team?.mainRegionId || "",
                partnerId: team?.partnerId || "",
            });

            navigate(ROUTES.RouteManagement);
        } catch (error) {
            const { displayMessage } = getApiError(error);

            notification.error({
                message: "Не вдалося створити маршрут",
                description: displayMessage,
            });
        }
    });

    const handleOpenDeleteModal = useCallback(() => {
        const selectedMarketIds = selectedSchedules.map(
            ({ marketId }) => marketId,
        );

        setModalState({
            type: RouteEditorModal.DeleteSchedule,
            payload: {
                marketIds: selectedMarketIds,
                isEmptyAfterDelete:
                    currentScheduleList.length === selectedMarketIds.length,
                createMode: true,
            },
        });
    }, [currentScheduleList.length, selectedSchedules]);

    const handleAfterDelete = async () => {
        exitMultiSelectMode();
    };

    const scheduleMenuConfig: ScheduleMenuConfig[] = [
        {
            type: "view",
            label: "Переглянути ТТ",
            onClick: (item: Schedule) => {
                navigate(
                    buildRoutePath(ROUTES.MarketDetails, {
                        marketId: item.marketId,
                    }),
                );
            },
        },
        {
            type: "edit",
            label: "Редагувати",
            onClick: (item: Schedule) => {
                setModalState({
                    type: RouteEditorModal.EditSchedule,
                    payload: {
                        schedule: item,
                        createMode: true,
                    },
                });
            },
        },
        {
            type: "delete",
            label: "Видалити",
            onClick: (item: Schedule) => {
                setModalState({
                    type: RouteEditorModal.DeleteSchedule,
                    payload: {
                        marketIds: [item.marketId],
                        isEmptyAfterDelete: currentScheduleList.length === 1,
                        createMode: true,
                    },
                });
            },
        },
    ];

    return (
        <>
            <Meta title="Редагувати маршрут" />

            {multiSelectMode ? (
                <MultiSelectorHeader
                    selectedCount={selectedSchedules.length}
                    allSelected={
                        sortedAndFilteredSchedules.length ===
                        selectedSchedules.length
                    }
                    onSelectAllItems={handleSelectAll}
                    onClearSelection={handleClearSelection}
                    onExitSelectionMode={exitMultiSelectMode}
                    onDeleteSelected={handleOpenDeleteModal}
                />
            ) : (
                <Header
                    title={"Створити маршрут"}
                    path={ROUTES.RouteManagement}
                />
            )}

            <MainContainer $paddingTop={"0"} $paddingBottom="8px">
                <div
                    style={{
                        overflowY: "auto",
                        paddingTop: 16,
                        height: "100%",
                    }}
                >
                    {!multiSelectMode && (
                        <RouteEditorForm form={routeEditorForm} />
                    )}

                    <FiltersPanel form={filtersForm} />

                    {schedulesLoading ? (
                        <BlockLoader />
                    ) : sortedAndFilteredSchedules?.length ? (
                        <ScheduleList
                            displaySchedules={sortedAndFilteredSchedules}
                            multiSelectMode={multiSelectMode}
                            selectedSchedules={selectedSchedules}
                            handleSelectSchedule={handleSelectSchedule}
                            handleTap={enterMultiSelectMode}
                            dayFilter={watchedFilters?.day}
                            scheduleMenuConfig={scheduleMenuConfig}
                            markets={markets}
                        />
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Немає активних точок"
                        />
                    )}
                </div>

                <Flex
                    vertical
                    gap={8}
                    style={{ paddingTop: 8, marginTop: "auto" }}
                >
                    <BaseButton
                        type="default"
                        onClick={handleAddMarketClick}
                        disabled={saveLoading}
                    >
                        Додати ТТ
                    </BaseButton>

                    <BaseButton
                        type="primary"
                        onClick={handleSave}
                        loading={saveLoading}
                    >
                        Зберегти
                    </BaseButton>
                </Flex>
            </MainContainer>

            <ModalsContainer
                onClose={handleCloseModal}
                modalState={modalState}
                onAfterConfirm={handleAfterDelete}
            />
        </>
    );
};
